/**
 * CLOCK MATH for schedules (RFC 0350 3a): when a local calendar rule is next
 * due, as a UTC instant.
 *
 * The zone is an IANA name, never a stored offset, so 9:00 stays 9:00 across a
 * daylight saving change. Two edges a stored offset would get wrong:
 *
 * - A NONEXISTENT local time (2:30 on a spring-forward night) runs at the next
 *   valid time that day: the instant the clocks jump, which reads as 3:00.
 * - An AMBIGUOUS local time (1:30 on a fall-back night) runs once, at its first
 *   occurrence. The second 1:30 is not after the first in wall time, so the
 *   next search moves on to the following day.
 *
 * Pure and dependency-free: `Intl.DateTimeFormat` resolves the zone, and
 * nothing here reads the process's own zone, so two devices in different zones
 * compute the same instant from the same rule.
 */

/** Daily or weekly at one local time. Weekdays are 0 (Sunday) to 6. */
export interface CalendarRule {
  every: 'day' | 'week'
  /** Local wall time, `HH:MM`, 24-hour. */
  time: string
  /** Required for `week`: which days. Ignored for `day`. */
  weekdays?: number[]
}

export type ScheduleTiming =
  | { kind: 'once'; at: string }
  | { kind: 'calendar'; rule: CalendarRule; timeZone: string }

interface Wall {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    formatters.set(timeZone, f)
  }
  return f
}

/** Whether `timeZone` is an IANA name this runtime knows. */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatter(timeZone)
    return true
  } catch {
    return false
  }
}

function wallClock(ms: number, timeZone: string): Wall {
  const parts: Record<string, number> = {}
  for (const p of formatter(timeZone).formatToParts(new Date(ms))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value)
  }
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
  }
}

/** A wall time as if it were UTC: comparable, and subtractable from an instant. */
function wallMs(w: Wall): number {
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second)
}

/** The zone's offset at `ms`, as wall minus UTC. */
function offsetAt(ms: number, timeZone: string): number {
  return wallMs(wallClock(ms, timeZone)) - Math.floor(ms / 1000) * 1000
}

const DAY = 24 * 60 * 60 * 1000

/**
 * The instant a local wall time names in `timeZone`, settling the two edges as
 * the header says: the earlier of two, or the jump instant when there is none.
 */
export function localToInstant(
  date: { year: number; month: number; day: number },
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const target = Date.UTC(date.year, date.month - 1, date.day, hour, minute)
  // The offsets either side of any transition near this time. Zones do not
  // change twice within three days, so these are the only two candidates.
  const before = offsetAt(target - 1.5 * DAY, timeZone)
  const after = offsetAt(target + 1.5 * DAY, timeZone)
  const candidates = [target - before, target - after]
  const valid = candidates
    .filter((t) => wallMs(wallClock(t, timeZone)) === target)
    .sort((a, b) => a - b)
  if (valid.length > 0) return valid[0]

  // A gap. Between the two candidates the wall clock jumps over `target`; the
  // first instant at or past it is the jump, which is the next valid time.
  let lo = Math.min(...candidates)
  let hi = Math.max(...candidates)
  while (hi - lo > 1000) {
    const mid = lo + Math.floor((hi - lo) / 2000) * 1000
    if (wallMs(wallClock(mid, timeZone)) >= target) hi = mid
    else lo = mid
  }
  return hi
}

function parseTime(time: string): { hour: number; minute: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time)
  if (!m) return null
  const hour = Number(m[1])
  const minute = Number(m[2])
  if (hour > 23 || minute > 59) return null
  return { hour, minute }
}

/** Why a timing cannot be scheduled, or null when it can. */
export function timingProblem(timing: ScheduleTiming): string | null {
  if (timing.kind === 'once') {
    return Number.isNaN(Date.parse(timing.at))
      ? `"${timing.at}" is not a date and time`
      : null
  }
  if (!isValidTimeZone(timing.timeZone)) {
    return `"${timing.timeZone}" is not an IANA time zone`
  }
  if (!parseTime(timing.rule.time)) {
    return `"${timing.rule.time}" is not a 24-hour HH:MM time`
  }
  if (timing.rule.every === 'week') {
    const days = timing.rule.weekdays ?? []
    if (days.length === 0) return 'a weekly rule needs at least one weekday'
    if (days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
      return 'weekdays are 0 (Sunday) to 6 (Saturday)'
    }
  }
  return null
}

/** The first calendar instant strictly after `afterMs`, or null. */
function calendarAfter(
  rule: CalendarRule,
  timeZone: string,
  afterMs: number,
): number | null {
  const time = parseTime(rule.time)
  if (!time) return null
  const days = rule.every === 'week' ? new Set(rule.weekdays ?? []) : null
  if (days && days.size === 0) return null

  // Start a day early: `afterMs` read in the zone may be a date behind the
  // instant it would produce on its own date. Eight more days covers any week.
  const start = wallClock(afterMs, timeZone)
  const origin = Date.UTC(start.year, start.month - 1, start.day)
  for (let i = -1; i <= 8; i++) {
    const d = new Date(origin + i * DAY)
    if (days && !days.has(d.getUTCDay())) continue
    const at = localToInstant(
      {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
      },
      time.hour,
      time.minute,
      timeZone,
    )
    if (at > afterMs) return at
  }
  return null
}

/**
 * The first due instant of a new or resumed schedule, as ISO UTC.
 *
 * A one-time schedule is due at its instant even when that is already past,
 * because a late reminder is delivered with its original time visible (3b). A
 * calendar rule is due at its next time after `nowMs`.
 */
export function firstOccurrence(
  timing: ScheduleTiming,
  nowMs: number,
): string | undefined {
  if (timing.kind === 'once') {
    const at = Date.parse(timing.at)
    return Number.isNaN(at) ? undefined : new Date(at).toISOString()
  }
  const next = calendarAfter(timing.rule, timing.timeZone, nowMs)
  return next === null ? undefined : new Date(next).toISOString()
}

/**
 * The occurrence after one that has been decided, as ISO UTC, or undefined
 * when there is none: a one-time schedule is spent once it has been decided.
 */
export function occurrenceAfter(
  timing: ScheduleTiming,
  afterMs: number,
): string | undefined {
  if (timing.kind === 'once') return undefined
  const next = calendarAfter(timing.rule, timing.timeZone, afterMs)
  return next === null ? undefined : new Date(next).toISOString()
}
